/**
 * Folder page tabs (owner D1, 28.09.2026): open, close, keep the title in sync.
 *
 * A folder page is a collection tab with a `folderId` (utils/collectionTabState
 * `hydrateFolderTabFromRecord`), so it lives in `collectionTabs`, in the same
 * strip order, and is persisted by the same snapshot as a collection page.
 *
 * Store-level on purpose: the sidebar row and every `{{chip}}` host
 * (utils/openFolderVariables) open a folder page, and the chip hosts are deep
 * inside the editor with no access to App's tab hooks.
 */
import {
  useTabsStore,
  setCollectionTabs,
  setTabStripOrder,
  setActiveCollectionTabId,
  setActiveTabType,
} from '../store/tabsStore';
import {
  useCollectionsStore,
  setSelectedCollection,
  setSelectedRequest,
  setSelectedFolder,
  setSelectedSchemaEntry,
} from '../store/collectionsStore';
import { appendTabStripEntry, removeTabStripEntry } from './workspaceTabPersistence';
import { hydrateFolderTabFromRecord, isFolderTab, isTabFor } from './collectionTabState';
import { getFolderById } from './collectionFolders';

const SECTIONS = new Set(['docs', 'authorization', 'variables', 'scripts']);

function newTabId() {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `fld-tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function select(collectionId, folder) {
  setSelectedFolder(folder ? { ...folder, collectionId } : null);
  setSelectedRequest(null);
  setSelectedCollection(null);
  setSelectedSchemaEntry(null);
}

/**
 * Open (or focus) the page of folder `folderId`. `section` picks the section
 * shown; without it an existing tab keeps the one the user left it on.
 * Returns the tab, or null when the collection or folder does not exist.
 */
export function openFolderTab(collectionId, folderId, { section } = {}) {
  if (collectionId == null || folderId == null) return null;
  const collection = (useCollectionsStore.getState().collections || [])
    .find((c) => String(c.id) === String(collectionId));
  const hydrated = collection ? hydrateFolderTabFromRecord(collection, folderId) : null;
  if (!hydrated) return null;
  const folder = { id: hydrated.folderId, name: hydrated.name };
  const wanted = SECTIONS.has(section) ? section : null;

  const existing = useTabsStore.getState().collectionTabs.find((t) => isTabFor(t, collectionId, folderId));
  if (existing) {
    // Only the section changes: the tab's draft (docs, auth, variables,
    // scripts) is the user's unsaved work and is never reseeded from the store.
    if (wanted && existing.section !== wanted) {
      setCollectionTabs((prev) => prev.map((t) => (t.id === existing.id ? { ...t, section: wanted } : t)));
    }
    setActiveCollectionTabId(existing.id);
    setActiveTabType('collection');
    select(collection.id, folder);
    return existing;
  }

  const tab = { id: newTabId(), type: 'collection', ...hydrated, ...(wanted ? { section: wanted } : {}) };
  setCollectionTabs((prev) => [...prev, tab]);
  setTabStripOrder((prev) => appendTabStripEntry(prev, { type: 'collection', id: tab.id }));
  setActiveCollectionTabId(tab.id);
  setActiveTabType('collection');
  select(collection.id, folder);
  return tab;
}

/**
 * Close every folder page of `collectionId` whose folder is in `folderIds`
 * (a deleted folder and its subfolders). Mirrors closeTab's "focus the last
 * remaining collection tab, else fall back to requests".
 */
export function closeFolderTabs(collectionId, folderIds) {
  const ids = new Set([...(folderIds || [])].map(String));
  const { collectionTabs, activeCollectionTabId, activeTabType } = useTabsStore.getState();
  const doomed = collectionTabs.filter(
    (t) => isFolderTab(t) && String(t.collectionId) === String(collectionId) && ids.has(String(t.folderId))
  );
  if (doomed.length === 0) return;
  const doomedIds = new Set(doomed.map((t) => t.id));
  const remaining = collectionTabs.filter((t) => !doomedIds.has(t.id));
  setCollectionTabs(remaining);
  setTabStripOrder((prev) => doomed.reduce((order, t) => removeTabStripEntry(order, 'collection', t.id), prev));
  if (!doomedIds.has(activeCollectionTabId)) return;
  const next = remaining[remaining.length - 1];
  setActiveCollectionTabId(next ? next.id : null);
  if (activeTabType === 'collection' && !next) setActiveTabType('request');
}

/**
 * Folder page titles follow the folder's name wherever the rename came from
 * (sidebar, MCP, realtime, git pull). A tab whose folder is gone keeps its
 * last title; the page itself says the folder no longer exists.
 */
export function syncFolderTabTitles(collections) {
  const { collectionTabs } = useTabsStore.getState();
  if (!collectionTabs.some(isFolderTab)) return;
  let changed = false;
  const next = collectionTabs.map((t) => {
    if (!isFolderTab(t)) return t;
    const col = (collections || []).find((c) => String(c.id) === String(t.collectionId));
    const folder = col ? getFolderById(col, t.folderId) : null;
    if (!folder || !folder.name || folder.name === t.name) return t;
    changed = true;
    return { ...t, name: folder.name };
  });
  if (changed) setCollectionTabs(next);
}
